import { RefreshCw } from "lucide-react";

export type MandateStatus =
  | "DRAFT"
  | "CREATED"
  | "PENDING_AUTH"
  | "ACTIVE"
  | "PAUSED"
  | "HALTED"
  | "CANCELLED"
  | "TERMINATED"
  | "COMPLETED"
  | "APPROVED"
  | "REJECTED"
  | "PENDING"
  | "INACTIVE"
  | "NOT_STARTED"
  | string;

export const STATUS_META: Record<
  string,
  { label: string; text: string; bg: string; dot: string }
> = {
  DRAFT: { label: "Draft", text: "text-slate-700", bg: "bg-slate-100 border border-slate-200", dot: "bg-slate-400" },
  CREATED: { label: "Created", text: "text-sky-700", bg: "bg-sky-50 border border-sky-200/60", dot: "bg-sky-500" },

  ACCEPTED: { label: "Accepted", text: "text-blue-700", bg: "bg-blue-50 border border-blue-200/60", dot: "bg-blue-500" },
  ACTIVATED: { label: "Active", text: "text-emerald-700", bg: "bg-emerald-50 border border-emerald-200/60", dot: "bg-emerald-500" },
  ACTIVE: { label: "Active", text: "text-emerald-700", bg: "bg-emerald-50 border border-emerald-200/60", dot: "bg-emerald-500" },
  PAUSED: { label: "Paused", text: "text-amber-700", bg: "bg-amber-50 border border-amber-200/60", dot: "bg-amber-500" },
  HALTED: { label: "Halted", text: "text-red-700", bg: "bg-red-50 border border-red-200/60", dot: "bg-red-500" },
  CANCELLED: { label: "Cancelled", text: "text-slate-600", bg: "bg-slate-100 border border-slate-200", dot: "bg-slate-400" },
  TERMINATED: { label: "Terminated", text: "text-rose-700", bg: "bg-rose-50 border border-rose-200/60", dot: "bg-rose-500" },
  COMPLETED: { label: "Completed", text: "text-purple-700", bg: "bg-purple-50 border border-purple-200/60", dot: "bg-purple-500" },
  PENDING_AUTH: { label: "Pending Auth", text: "text-amber-700", bg: "bg-amber-50 border border-amber-200/60", dot: "bg-amber-500" },
  AUTH_PENDING: { label: "Auth Pending", text: "text-amber-700", bg: "bg-amber-50 border border-amber-200/60", dot: "bg-amber-500" },
  INITIATED: { label: "Initiated", text: "text-sky-700", bg: "bg-sky-50 border border-sky-200/60", dot: "bg-sky-500" },
  APPROVED: { label: "Approved", text: "text-emerald-700", bg: "bg-emerald-50 border border-emerald-200/60", dot: "bg-emerald-500" },
  REJECTED: { label: "Rejected", text: "text-red-700", bg: "bg-red-50 border border-red-200/60", dot: "bg-red-500" },
  PENDING: { label: "Pending", text: "text-amber-700", bg: "bg-amber-50 border border-amber-200/60", dot: "bg-amber-500" },
  INACTIVE: { label: "Inactive", text: "text-slate-600", bg: "bg-slate-100 border border-slate-200", dot: "bg-slate-400" },
  NOT_STARTED: { label: "Not Started", text: "text-slate-600", bg: "bg-slate-100 border border-slate-200", dot: "bg-slate-400" },

  // Installment States
  COLLECTION_INITIATED: { label: "Collection Initiated", text: "text-blue-700", bg: "bg-blue-50 border border-blue-200/60", dot: "bg-blue-500" },
  COLLECTION_SUCCESS: { label: "Collection Success", text: "text-emerald-700", bg: "bg-emerald-50 border border-emerald-200/60", dot: "bg-emerald-500" },
  COLLECTION_FAILED: { label: "Collection Failed", text: "text-red-700", bg: "bg-red-50 border border-red-200/60", dot: "bg-red-500" },
  SETTLEMENT_INITIATED: { label: "Settlement Initiated", text: "text-violet-700", bg: "bg-violet-50 border border-violet-200/60", dot: "bg-violet-500" },
  SETTLEMENT_SUCCESS: { label: "Settlement Success", text: "text-teal-700", bg: "bg-teal-50 border border-teal-200/60", dot: "bg-teal-500" },
  SETTLEMENT_FAILED: { label: "Settlement Failed", text: "text-red-700", bg: "bg-red-50 border border-red-200/60", dot: "bg-red-500" },
  COLLECTED: { label: "Collected", text: "text-emerald-700", bg: "bg-emerald-50 border border-emerald-200/60", dot: "bg-emerald-500" },
  FAILED: { label: "Failed", text: "text-red-700", bg: "bg-red-50 border border-red-200/60", dot: "bg-red-500" },
  SKIPPED: { label: "Skipped", text: "text-slate-600", bg: "bg-slate-100 border border-slate-200", dot: "bg-slate-400" },
  IN_PROCESS: { label: "In Process", text: "text-sky-700", bg: "bg-sky-50 border border-sky-200/60", dot: "bg-sky-500" },
  SCHEDULED: { label: "Scheduled", text: "text-blue-700", bg: "bg-blue-50 border border-blue-200/60", dot: "bg-blue-500" },
  SETTLED: { label: "Settled", text: "text-teal-700", bg: "bg-teal-50 border border-teal-200/60", dot: "bg-teal-500" },
};

const DEFAULT_META = { label: "Unknown", text: "text-slate-600", bg: "bg-slate-100", dot: "bg-slate-400" };

interface StatusBadgeProps {
  status: MandateStatus;
  size?: "sm" | "md";
  onRefresh?: (e: React.MouseEvent) => void;
  isRefreshing?: boolean;
}

export default function StatusBadge({
  status,
  size = "md",
  onRefresh,
  isRefreshing = false,
}: StatusBadgeProps) {
  if (!status) return null;
  const key = String(status).toUpperCase().replace(/\s+/g, "_");
  const meta = STATUS_META[key] || {
    ...DEFAULT_META,
    label: String(status),
  };

  const badgeContent = (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-medium ${meta.bg} ${meta.text} ${
        size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs"
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
      <span>{meta.label}</span>
      {onRefresh && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRefresh(e);
          }}
          disabled={isRefreshing}
          title="Fetch latest status from gateway"
          className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full p-0.5 text-current hover:bg-black/10 transition cursor-pointer disabled:opacity-50"
        >
          <RefreshCw className={`h-2.5 w-2.5 ${isRefreshing ? "animate-spin" : ""}`} />
        </button>
      )}
    </span>
  );

  return badgeContent;
}